// Marketing service. Reads + writes against `marketing.campaigns`,
// `marketing.campaign_recipients`, `marketing.sms_templates`.
//
// Module: marketing (UI module ID; covers `marketing.*`).

import { supabase } from '../client';
import { requireContext, type ActiveContext } from '../activeContext';
import {
  getClientDisplayNamesByIds,
  lookupClientsByPhoneDigits,
} from './clients';
import { phoneDigitsOnly } from './sms';

/** See products.ts for why this shim exists. */
const sbUntyped = (): any => supabase() as unknown as any; // eslint-disable-line @typescript-eslint/no-explicit-any
import {
  applyKeyset,
  buildNextCursor,
  clampPageSize,
  selectCountOption,
  type KeysetInput,
  type ListResult,
} from '../pagination';

// Mirrors the live Postgres enum `marketing.campaign_status` exactly
// (draft,queued,sending,completed,failed,cancelled,archived). Do NOT
// reintroduce 'scheduled'/'sent' — those labels don't exist in the DB and a
// status filter/cast using them throws 22P02. See the marketing dossier footgun.
export type CampaignStatus =
  | 'draft'
  | 'queued'
  | 'sending'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'archived';

export interface MarketingCampaign {
  id: string;
  org_id: string;
  store_id: string | null;
  name: string;
  message_text: string;
  status: CampaignStatus;
  is_active: boolean;
  sender_provider: string | null;
  sender_provider_user_id: string | null;
  sender_from_number: string | null;
  sender_name: string | null;
  total_recipients: number;
  successful_sends: number;
  failed_sends: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// Mirrors the live Postgres enum `marketing.recipient_status` exactly
// (pending,sent,failed,delivered,opted_out,invalid_number). The initial state
// is 'pending' (NOT 'queued' — that label doesn't exist and inserting it
// throws 22P02).
export type RecipientStatus =
  | 'pending'
  | 'sent'
  | 'failed'
  | 'delivered'
  | 'opted_out'
  | 'invalid_number';

export interface CampaignRecipient {
  id: string;
  org_id: string;
  campaign_id: string;
  client_id: string | null;
  phone_number: string;
  status: RecipientStatus;
  provider_message_id: string | null;
  error_message: string | null;
  sent_at: string | null;
  created_at: string;
  /** Resolved from client_id or store-scoped phone match. */
  client_display_name?: string | null;
  /** Client row to link when name is shown. */
  resolved_client_id?: string | null;
}

export interface SmsTemplate {
  id: string;
  org_id: string;
  store_id: string | null;
  name: string;
  body: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// ---------- campaigns ----------------------------------------------------

export interface ListCampaignsInput extends KeysetInput {
  search?: string;
  status?: CampaignStatus;
  storeId?: string | null;
}

export async function listCampaigns(
  ctx: ActiveContext,
  input: ListCampaignsInput = {},
): Promise<ListResult<MarketingCampaign>> {
  const { orgId } = requireContext(ctx);
  const pageSize = clampPageSize(input.pageSize);

  let q = sbUntyped()
    .schema('marketing')
    .from('campaigns')
    .select('*', selectCountOption(input.countMode))
    .eq('org_id', orgId);

  if (input.status) q = q.eq('status', input.status);
  if (input.storeId !== undefined && input.storeId !== null) {
    q = q.eq('store_id', input.storeId);
  }
  if (input.search && input.search.trim().length > 0) {
    const s = `%${input.search.trim()}%`;
    q = q.or([`name.ilike.${s}`, `message_text.ilike.${s}`].join(','));
  }

  q = applyKeyset(q, input.cursor, 'updated_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as unknown as MarketingCampaign[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'updated_at',
    ),
    total: count ?? null,
    totalIsExact: input.countMode === 'exact',
  };
}

export async function getCampaign(id: string): Promise<MarketingCampaign | null> {
  const { data, error } = await sbUntyped()
    .schema('marketing')
    .from('campaigns')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as unknown as MarketingCampaign | null;
}

export async function createCampaign(
  ctx: ActiveContext,
  input: {
    name: string;
    message_text: string;
    store_id?: string | null;
    status?: CampaignStatus;
    sender_from_number?: string | null;
    sender_name?: string | null;
  },
): Promise<MarketingCampaign> {
  const { orgId } = requireContext(ctx);
  const { data, error } = await sbUntyped()
    .schema('marketing')
    .from('campaigns')
    .insert({
      org_id: orgId,
      name: input.name,
      message_text: input.message_text,
      store_id: input.store_id ?? null,
      status: input.status ?? 'draft',
      is_active: true,
      sender_from_number: input.sender_from_number ?? null,
      sender_name: input.sender_name ?? null,
      total_recipients: 0,
      successful_sends: 0,
      failed_sends: 0,
    } as never)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as MarketingCampaign;
}

export async function updateCampaign(
  id: string,
  patch: Partial<
    Pick<
      MarketingCampaign,
      'name' | 'message_text' | 'status' | 'is_active' | 'sender_from_number' | 'sender_name'
    >
  >,
): Promise<MarketingCampaign> {
  const { data, error } = await sbUntyped()
    .schema('marketing')
    .from('campaigns')
    .update(patch as never)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as MarketingCampaign;
}

export async function archiveCampaign(id: string): Promise<void> {
  const { error } = await sbUntyped()
    .schema('marketing')
    .from('campaigns')
    .update({ status: 'archived', is_active: false } as never)
    .eq('id', id);
  if (error) throw error;
}

// ---------- recipients ---------------------------------------------------

export async function listRecipientsForCampaign(
  ctx: ActiveContext,
  campaignId: string,
  opts: { limit?: number; storeId?: string | null } = {},
): Promise<CampaignRecipient[]> {
  const { data, error } = await sbUntyped()
    .schema('marketing')
    .from('campaign_recipients')
    .select('*')
    .eq('campaign_id', campaignId)
    .order('created_at', { ascending: true })
    .limit(opts.limit ?? 500);
  if (error) throw error;
  const recipients = (data ?? []) as unknown as CampaignRecipient[];
  const storeId = opts.storeId ?? null;
  if (recipients.length === 0 || !storeId) return recipients;

  const clientIds = recipients
    .map((r) => r.client_id)
    .filter((id): id is string => Boolean(id));
  const namesById = await getClientDisplayNamesByIds(ctx, clientIds);

  const unresolvedDigits = new Set<string>();
  for (const r of recipients) {
    const fromId = r.client_id ? namesById.get(r.client_id) : null;
    if (fromId) continue;
    const digits = phoneDigitsOnly(r.phone_number);
    if (digits) unresolvedDigits.add(digits);
  }

  const byPhone = await lookupClientsByPhoneDigits(
    ctx,
    storeId,
    [...unresolvedDigits],
  );

  return recipients.map((r) => {
    if (r.client_id && namesById.has(r.client_id)) {
      return {
        ...r,
        client_display_name: namesById.get(r.client_id)!,
        resolved_client_id: r.client_id,
      };
    }
    const digits = phoneDigitsOnly(r.phone_number);
    const match = digits ? byPhone.get(digits) : undefined;
    if (match) {
      return {
        ...r,
        client_display_name: match.displayName,
        resolved_client_id: match.clientId,
      };
    }
    return r;
  });
}

export async function addRecipientsToCampaign(
  ctx: ActiveContext,
  campaignId: string,
  recipients: Array<{ client_id?: string | null; phone_number: string }>,
): Promise<number> {
  const { orgId } = requireContext(ctx);
  if (recipients.length === 0) return 0;
  const rows = recipients.map((r) => ({
    org_id: orgId,
    campaign_id: campaignId,
    client_id: r.client_id ?? null,
    phone_number: r.phone_number,
    status: 'pending' as const,
  }));
  const { error, count } = await sbUntyped()
    .schema('marketing')
    .from('campaign_recipients')
    .insert(rows as never, { count: 'exact' });
  if (error) throw error;
  return count ?? rows.length;
}

// ---------- sms templates ------------------------------------------------

export async function listSmsTemplates(ctx: ActiveContext): Promise<SmsTemplate[]> {
  const { orgId } = requireContext(ctx);
  const { data, error } = await sbUntyped()
    .schema('marketing')
    .from('sms_templates')
    .select('*')
    .eq('org_id', orgId)
    .order('name', { ascending: true })
    .limit(500);
  if (error) throw error;
  return (data ?? []) as unknown as SmsTemplate[];
}

export async function upsertSmsTemplate(
  ctx: ActiveContext,
  input: { id?: string; name: string; body: string; store_id?: string | null },
): Promise<SmsTemplate> {
  const { orgId } = requireContext(ctx);
  if (input.id) {
    const { data, error } = await sbUntyped()
      .schema('marketing')
      .from('sms_templates')
      .update({ name: input.name, body: input.body, store_id: input.store_id ?? null } as never)
      .eq('id', input.id)
      .select()
      .single();
    if (error) throw error;
    return data as unknown as SmsTemplate;
  }
  const { data, error } = await sbUntyped()
    .schema('marketing')
    .from('sms_templates')
    .insert({
      org_id: orgId,
      name: input.name,
      body: input.body,
      store_id: input.store_id ?? null,
    } as never)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as SmsTemplate;
}

export async function deleteSmsTemplate(id: string): Promise<void> {
  const { error } = await sbUntyped()
    .schema('marketing')
    .from('sms_templates')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ---------- summary ------------------------------------------------------

export interface MarketingSummary {
  total_campaigns: number;
  sent_campaigns: number;
  pending_campaigns: number;
  templates_count: number;
}

export async function getMarketingSummary(
  ctx: ActiveContext,
): Promise<MarketingSummary> {
  const { orgId } = requireContext(ctx);
  const [allQ, sentQ, draftQ, templatesQ] = await Promise.all([
    sbUntyped()
      .schema('marketing')
      .from('campaigns')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', orgId),
    sbUntyped()
      .schema('marketing')
      .from('campaigns')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', orgId)
      .eq('status', 'completed'),
    sbUntyped()
      .schema('marketing')
      .from('campaigns')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', orgId)
      .in('status', ['draft', 'queued', 'sending']),
    sbUntyped()
      .schema('marketing')
      .from('sms_templates')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', orgId),
  ]);
  return {
    total_campaigns: allQ.count ?? 0,
    sent_campaigns: sentQ.count ?? 0,
    pending_campaigns: draftQ.count ?? 0,
    templates_count: templatesQ.count ?? 0,
  };
}
